/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import Coverage from '../Models/Coverage';
import '@xengage/gw-platform-translations';
import MLEditableCovTermComponent from '../MLEditableCovTermComponent/MLEditableCovTermComponent';
import MLClauseScheduledItemsComponent from '../MLClauseScheduledItemsComponent/MLClauseScheduledItemsComponent';
import metadata from './MLEditableCoveragesComponent.metadata.json5';
import styles from './MLEditableCoveragesComponent.module.scss';
import messages from './MLEditableCoveragesComponent.messages';

function MLEditableCoveragesComponent(props) {
    const translator = useTranslator();

    const {
        coverages,
        loading,
        onCovChange,
        onGeneralChange,
        allowSelectCoverage,
        displayHelpMessage,
        optionalHasCheck,
        filterData,
        isAgent,
        showPremium,
        showMonthlyPremium,
        monthlyPremiumEval,
        isQuoteStale,
        isOptionalCoverage,
        isOccupancySecondaryOrSeasonal,
        isModernProduct,
        isNotDwellingPolicy
    } = props;

    const [model, setModel] = useState([]);
    const DELIMITER = '####';

    const isLoading = () => loading;

    useEffect(() => {
        let updatedModel = [];
        if (coverages) {
            updatedModel = coverages.map(_.partial(Coverage.create, _, isLoading, onCovChange));
        }
        updatedModel = updatedModel.filter((cov) => cov !== undefined);
        setModel(updatedModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coverages]);

    const coverageSelectedChanged = useCallback((isSelected, coverage) => {
        coverage.selected = isSelected;
    }, []);

    const isDescriptionDuplicated = (coverage) => {
        return coverage.name === coverage.description;
    };

    const hasReadMore = (cov) => {
        return cov.description ? cov.description.includes(DELIMITER) : false;
    };

    const displayBeforeReadMoreContent = (cov) => {
        return cov.description && cov.description.split(DELIMITER)[0];
    };

    const getPopoverContent = useCallback((cov) => {
        if (hasReadMore(cov)) {
            return cov.description.split(DELIMITER)[1];
        }
        return cov.description;
    }, []);

    const isNotSPPAndVPP = (cov) => {
        return cov.code_ML !== 'HOPValuablePersonalProperty' && cov.code_ML !== 'HOPScheduledPersonalProperty';
    };

    const covToHide = (cov) => {
        if(!isModernProduct){
            return true;
        }
        return cov.code_ML !== "HOPReplacementCostOnPersonalPropertyCov_Ext" && cov.code_ML !== "HOPRoofDamageExclusionCov_Ext";
    };

    const isSelfInsuredRetention = (cov) => {
        return cov.code_ML === 'PUPSelfInsuredRetention_ML';
    };


    const resolvers = {
        resolveComponentMap: {
            mleditablecovtermcomponent: MLEditableCovTermComponent,
            mlclausescheduleitemscomponent: MLClauseScheduledItemsComponent
        },
        resolveClassNameMap: styles
    };

    const generateCoverageOverrides = useCallback(() => {
        const coverageOverrides = model.map((cov, covIndex) => {
            return {
                [`covIterableContainer${covIndex}`]: {
                    visible: isNotSPPAndVPP(cov) && covToHide(cov)
                },
                [`requiredCoverageCheckboxDiv${covIndex}`]: {
                    visible: cov.required
                },
                [`requiredCoverageIcon${covIndex}`]: {
                    visible: cov.required
                },
                [`requiredCoverageName${covIndex}`]: {
                    content: cov.name
                },
                [`requiredCoverageTooltip${covIndex}`]: {
                    visible: cov.description && displayHelpMessage,
                    tooltipContent: cov.description,
                    noHeader: true
                },
                [`nonRequiredCoverageCheckboxDiv${covIndex}`]: {
                    visible: !cov.required
                },
                [`nonRequiredCoverageCheckbox${covIndex}`]: {
                    label: cov.name,
                    onValueChange: coverageSelectedChanged,
                    path: cov,
                    value: cov.selected
                },
                [`nonRequiredCoverageTooltipParagraph${covIndex}`]: {
                    visible: hasReadMore(cov),
                },
                [`nonRequiredCoverageTooltipLabel${covIndex}`]: {
                    content: displayBeforeReadMoreContent(cov)
                },
                [`nonRequiredCoverageTooltip${covIndex}`]: {
                    tooltipContent: getPopoverContent(cov),
                    noHeader: true
                },
                [`covTermTable${covIndex}`]: {
                    className: cov.selected ? 'flex-table border-top-none' : 'flex-table',
                    visible: cov.terms.length >= 1
                },
                [`covTermTableRowHeader${covIndex}`]: {
                    className: showPremium || showMonthlyPremium ? 'table-row table-header three-columns' : 'table-header table-row two-columns',
                    visible: isOptionalCoverage
                },
                [`covTermHeaderLimits${covIndex}`]: {
                    className: showPremium || showMonthlyPremium ? 'mid-column light font-weight-bold' : 'right-column light font-weight-bold'
                },
                [`covTermHeaderPremium${covIndex}`]: {
                    visible: !!(showPremium || showMonthlyPremium),
                    content: showMonthlyPremium ? translator(messages.monthlyPremium) : translator(messages.premium)
                },
                [`covTermHeaderPremium${covIndex}`]: {
                    visible: !!(showPremium || showMonthlyPremium),
                    content: showMonthlyPremium ? translator(messages.monthlyPremium) : translator(messages.premium)
                },
                [`covTermComponent${covIndex}`]: {
                    coverage: cov,
                    index: covIndex,
                    displayBeforeReadMoreContent,
                    displayHelpMessage,
                    isDescriptionDuplicated,
                    hasReadMore,
                    getPopoverContent,
                    isSelfInsuredRetention,
                    showPremium,
                    showMonthlyPremium,
                    isOccupancySecondaryOrSeasonal,
                    isModernProduct,
                    onCovChange
                },
                [`covScheduleItems${covIndex}`]: {
                    visible: (cov.required || cov.selected) && cov.scheduledItems_ML,
                    clause: cov,
                    onGeneralChange,
                    onCovChange,
                    loading,
                    isQuoteStale,
                    readOnly: false,
                    isNotDwellingPolicy
                }
            };
        });

        return Object.assign({}, ...coverageOverrides);
    }, [coverageSelectedChanged, displayHelpMessage, getPopoverContent, isAgent, isQuoteStale, loading, model, onCovChange, onGeneralChange,
        showMonthlyPremium, showPremium, translator]);

    const overrides = {
        covContainer: {
            visible: !_.isEmpty(model)
        },
        covIterable: {
            data: model
        },
        ...generateCoverageOverrides()
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLEditableCoveragesComponent.propTypes = {
    coverages: PropTypes.shape({}),
    showMonthlyPremium: PropTypes.bool,
    optionalHasCheck: PropTypes.bool,
    loading: PropTypes.bool,
    onCovChange: PropTypes.func,
    onGeneralChange: PropTypes.func,
    allowSelectCoverage: PropTypes.bool,
    displayHelpMessage: PropTypes.bool,
    filterData: PropTypes.bool,
    isAgent: PropTypes.bool,
    showPremium: PropTypes.bool,
    monthlyPremiumEval: PropTypes.func,
    isQuoteStale: PropTypes.bool,
    isOccupancySecondaryOrSeasonal: PropTypes.bool,
    isModernProduct : PropTypes.bool
};

MLEditableCoveragesComponent.defaultProps = {
    optionalHasCheck: true,
    isAgent: false,
    isOptionalCoverage: false
};

export default MLEditableCoveragesComponent;
